import React, { useEffect, useState, useCallback, useRef } from "react";
import { RadarChart } from "../charts/RadarChart";
import { LineChart } from "../charts/LineChart";
import { BarColumnChart } from "../charts/BarColumnChart";
import { getFootprintData } from "../../services/summaryReportsService";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { MonthNavigator } from "./MonthNavigator";
import { ChartContainerMessage } from "./ChartContainerMessage";
import {
	getDateFromEpoch,
	getMonthAbbreviation,
	getDateOrdinal,
} from "../../services/dateService";

export const FootPrintChartContainer = ({ code, type }) => {
	const [dataSet, setDataSet] = useState([]);

	const [def, setDef] = useState(null);
	const [hasErrored, setHasErrored] = useState(false);

	let currentDate = dataSet.date ?? null;

	const mounted = useRef(false);

	const getData = (dateToGet) => {
		const then = (responseData) => {
			if (mounted.current === true) {
				currentDate = responseData.data.date;
				// do we need to process for local date?
				if (responseData.data.dueDate) {
					const dateFromEpoch = getDateFromEpoch(responseData.data.dueDate);
					const date = dateFromEpoch.getDate();
					const ordinal = getDateOrdinal(date);
					const month = getMonthAbbreviation(dateFromEpoch.getMonth());
					const year = dateFromEpoch.getFullYear();

					responseData.data.title = `${date}${ordinal} ${month} ${year}`;
				}

				setDataSet(responseData.data);
				setDef(responseData.def);
			}
		};

		if (dateToGet === null) {
			getFootprintData(code)
				.then(then)
				.catch((e) => {
					if (mounted.current === true) {
						setHasErrored(true);
					}
				});
		} else {
			getFootprintData(code, dateToGet)
				.then(then)
				.catch((e) => {
					if (mounted.current === true) {
						setHasErrored(true);
					}
				});
		}
	};

	useEffect(() => {
		mounted.current = true;
		getData(currentDate);

		return () => {
			mounted.current = false;
		};
	}, []);

	const first = () => {
		if (dataSet.nav.first === currentDate) {
			return;
		}
		getData(dataSet.nav.first);
	};

	const back = () => {
		if (dataSet.nav.prev === null) {
			return;
		}
		getData(dataSet.nav.prev);
	};

	const next = () => {
		if (dataSet.nav.next === null) {
			return;
		}
		getData(dataSet.nav.next);
	};

	const last = () => {
		if (dataSet.nav.last === currentDate) {
			return;
		}
		getData(dataSet.nav.last);
	};

	if (hasErrored === true) {
		return (
			<ChartContainerMessage
				message={[
					"It has not been possible to retrieve your report.",
					"This is most likely because the connection to the internet has been lost.",
					"If this problem persists, please contact our helpdesk, quoting reference '#FP001'.",
				]}
			/>
		);
	}

	if (def === null) {
		return <LoadingSpinner asFullscreenOverlay={false} />;
	}

	let chart = null;

	if (dataSet !== null) {
		switch (def.subType.toLowerCase()) {
			case "bar":
			case "column":
				chart = (
					<BarColumnChart def={def} dataSets={[dataSet]} type={def.subType} />
				);
				break;
			case "line":
				chart = <LineChart def={def} dataSets={[dataSet]} />;
				break;
			case "radar":
			case "radararea":
			case "radarcolumn":
				chart = <RadarChart def={def} dataSet={dataSet} type={def.subType} />;
				break;
			default:
				chart = <RadarChart def={def} dataSet={dataSet} type={"radarArea"} />;

				break;
		}
	}

	if (dataSet) {
		if (!dataSet.data || dataSet.data.length === 0) {
			chart = (
				<ChartContainerMessage
					message={"There is no data to display for this period"}
				/>
			);
		}
	}

	return (
		<div className={"h-full flex flex-col"}>
			<MonthNavigator
				heading={dataSet === null ? "Loading" : dataSet.title}
				first={first}
				back={back}
				next={next}
				last={last}
				nav={dataSet ? dataSet.nav : null}
			/>
			<div className={"flex-1"}>{chart}</div>

			{def !== null && def.format.score ? (
				<div className={"w-full text-center text-sm"}>
					{def === null || dataSet === null ? (
						"Loading"
					) : (
						<label>
							{def.format.score.display}{" "}
							<span className={"font-bold"}>
								{dataSet.score ?? 0} {def.format.score.suffix}
							</span>
						</label>
					)}
				</div>
			) : null}
		</div>
	);
};
